import { FC } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { CommonPanel } from '../CommonPanel';
import { InfoButton } from '../InfoButton';
import { BuyButton } from '../BuyButton';
import { PlayersCards } from '../PlayersCards';
import { PrimaryPanelText } from '../PrimaryPanelText';
import { GamePart } from '../GamePart';
import { GameType } from '../GameType';
import { BingoCardPattern } from '../BingoCardPattern';
import { NextGamePanelProps } from './mobile';
import { LiveIndicator } from '../LiveIndicator';
import { Ball30 } from '../Ball30';
import { Ball75 } from '../Ball75';
import { Ball80 } from '../Ball80';
import { Ball90 } from '../Ball90';
import fireBallGameDetails from '../../assets/apng/fireball-game-details.png';

const BALL_COMPONENTS = {
    30: Ball30,
    75: Ball75,
    80: Ball80,
    90: Ball90,
};

const NextGamePanelContainer = styled(CommonPanel)`
    position: relative;
    height: auto;
    margin-bottom: 0.5rem;
    width: 89.8%;
`;

const NextGamePart = styled(GamePart)`
    width: 100%;
    justify-content: flex-end;
`;

const UpperContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    margin-bottom: 0.5rem;
`;

const UpperTextContainer = styled.div<{
    isGameLive: boolean;
    hasHeaderFireBall: boolean;
}>`
    display: flex;
    flex-direction: column;
    flex-basis: ${({ isGameLive, hasHeaderFireBall }) => {
        if (isGameLive) {
            return hasHeaderFireBall ? '56.5%' : '64.5%';
        }

        return hasHeaderFireBall ? '63.5%' : '71.5%';
    }};
    margin-right: 1rem;
`;

const UpperImageContainer = styled.div<{
    isGameLive: boolean;
    hasHeaderFireBall: boolean;
}>`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    column-gap: 0.375rem;
    flex-basis: ${({ isGameLive, hasHeaderFireBall }) => {
        if (isGameLive) {
            return hasHeaderFireBall ? '29%' : '21%';
        }

        return hasHeaderFireBall ? '22%' : '14%';
    }};
`;

const HeaderFireBall = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.125rem;
    height: 3.125rem;
    flex: 0 0 3.125rem;
`;

const HeaderFireBallImage = styled.img`
    position: absolute;
    top: 50%;
    left: 50%;
    width: calc(100% + 1.25rem);
    height: calc(100% + 1.25rem);
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 1;
`;

const Divider = styled.hr`
    width: 100%;
    margin-left: 0;
    border-width: 0;
    height: 0.125rem;
    background-color: ${({ theme: { primaryPanel } }) =>
        primaryPanel.color.divider};
`;

const LowerContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 87%;
`;

const LowerTextContainer = styled.div<{ gameType: number }>`
    display: flex;
    justify-content: center;
    flex-direction: column;
`;

const FlexRowWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
`;

const BingoCardPatternWrapper = styled(BingoCardPattern)`
    position: relative;
    top: 3px;
`;

const FlexColWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    row-gap: 0.25rem;
`;

const PlayersCardsWrapper = styled(PlayersCards)`
    height: 2.5rem;
`;

const BuyButtonWrapper = styled(BuyButton)`
    position: absolute;
    top: 0;
    right: -7rem;
    bottom: 0;
    margin: auto 0;
`;

export const GameContinuedIcon = styled.img`
    width: 2rem;
    height: 2rem;
    align-self: flex-end;
`;

export const NextGamePanelDesktop: FC<NextGamePanelProps> = ({
    to,
    seconds,
    gameTitle,
    patternName,
    ticketPrice,
    gamePrize,
    gamePlayers,
    gameCards,
    hasJackpot,
    jackpotName,
    jackpotCall,
    jackpotPrize,
    currentPart,
    gameParts,
    fireBall,
    gameType,
    patternType,
    parts,
    gameLinked,
    gameContinued,
    showTotalTickets,
    autoBuy,
    onInfoBox,
    onBuy,
    isGameLive,
    isPatternNameRendered,
    ...props
}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const { starImage4 } = theme.images;
    const NEXTGAME_GAME = t('infoBox.nextGame');
    const FireBall = BALL_COMPONENTS[gameType];
    const showFireBall = !!fireBall && fireBall > 0;

    return (
        <NextGamePanelContainer {...props} type="primary">
            <UpperContainer>
                <UpperTextContainer
                    isGameLive={isGameLive}
                    hasHeaderFireBall={showFireBall}
                >
                    <PrimaryPanelText
                        label={NEXTGAME_GAME}
                        value={gameTitle}
                        type="big"
                        gameLinked={gameLinked}
                    />
                    <Divider />
                </UpperTextContainer>
                <UpperImageContainer
                    isGameLive={isGameLive}
                    hasHeaderFireBall={showFireBall}
                >
                    {isGameLive && <LiveIndicator />}
                    {showFireBall && FireBall && (
                        <HeaderFireBall>
                            <FireBall number={fireBall} size="xxsmall" />
                            <HeaderFireBallImage
                                src={fireBallGameDetails}
                                alt=""
                            />
                        </HeaderFireBall>
                    )}
                    <InfoButton handleClick={onInfoBox} />
                    <GameType gameType={gameType} />
                </UpperImageContainer>
            </UpperContainer>
            <LowerContainer>
                <LowerTextContainer gameType={gameType}>
                    {isPatternNameRendered && (
                        <PrimaryPanelText
                            label={patternName}
                            value={gamePrize}
                            type="small"
                        />
                    )}
                    {hasJackpot && (
                        <PrimaryPanelText
                            label={t('infoBox.jackpotFormatting', {
                                jpName: jackpotName,
                                jpCall: jackpotCall,
                            })}
                            value={jackpotPrize || ''}
                            type="small"
                        />
                    )}
                </LowerTextContainer>
                <FlexColWrapper>
                    {gameContinued ? (
                        <GameContinuedIcon
                            src={starImage4}
                            alt="Continued Game Icon"
                        />
                    ) : (
                        <NextGamePart
                            gameParts={gameParts}
                            currentPart={currentPart}
                            fireBall={fireBall}
                        />
                    )}

                    <FlexRowWrapper>
                        <BingoCardPatternWrapper
                            patternType={patternType}
                            parts={parts}
                            currentPart={currentPart - 1}
                            gameType={gameType}
                        />
                        {showTotalTickets && (
                            <>
                                <PlayersCardsWrapper
                                    type="players"
                                    text={gamePlayers}
                                />
                                <PlayersCardsWrapper
                                    type="cards"
                                    text={gameCards}
                                />
                            </>
                        )}
                    </FlexRowWrapper>
                </FlexColWrapper>
            </LowerContainer>
            <BuyButtonWrapper
                isDisabled={seconds <= 20}
                autoBuy={autoBuy}
                cardCost={ticketPrice}
                handleClick={onBuy}
            />
        </NextGamePanelContainer>
    );
};
